import { Loader2, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { buildPapDetail } from '../features/ttqib/papDisplay';
import type { KnowledgeParticlePapView } from '../services/knowledgeService';
import { MathTextBlock } from './MathTextBlock';

function extractionStatusLabel(status: KnowledgeParticlePapView['extractionStatus'] | undefined): string {
  switch (status) {
    case 'pending': return '进行中…';
    case 'running': return '正在分析…';
    case 'failed': return '分析失败';
    case 'ready': return '提取完成';
    case 'partial': return '部分完成';
    default: return '';
  }
}

export function KnowledgeParticlePapStubPanel({
  papView,
  loading = false,
  error = null,
}: {
  papView: KnowledgeParticlePapView | null;
  loading?: boolean;
  error?: string | null;
}) {
  const detail = buildPapDetail(papView?.pap ?? null);
  const pending = loading
    || papView?.extractionStatus === 'running'
    || papView?.extractionStatus === 'pending';
  const failed = papView?.extractionStatus === 'failed';
  const failedWithoutPap = failed && !detail.hasContent;
  const statusText = extractionStatusLabel(papView?.extractionStatus) || '正在分析…';

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col">
      <div className="border-b border-primary/10 bg-[#DCEBFF] px-2 py-1.5">
        <p className="text-[11px] font-extrabold tracking-wide text-primary-strong">隐性能力</p>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-hidden px-2 py-2">
        {pending ? (
          <div className="flex items-center gap-1.5 text-[11px] leading-4 text-muted">
            <Loader2 size={12} className="shrink-0 animate-spin text-primary-strong" />
            <span>{statusText}</span>
          </div>
        ) : null}
        {!pending && (failedWithoutPap || error) ? (
          <p className="text-[11px] leading-4 text-danger">
            {papView?.failure?.message || error || '分析失败'}
          </p>
        ) : null}
        {!pending && !failedWithoutPap && detail.hasContent ? (
          <div className="space-y-2">
            {detail.matchedTags.length ? (
              <div className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-wide text-subtle">MindSet 匹配</p>
                {detail.matchedTags.slice(0, 4).map((tag, index) => (
                  <div key={`${tag.label}-${index}`} className="flex items-start gap-1.5">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-strong" />
                    <MathTextBlock text={tag.label} className="min-w-0 text-[11px] font-semibold leading-4 text-ink" />
                  </div>
                ))}
              </div>
            ) : null}
            {detail.implicitSteps.length ? (
              <div className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-wide text-subtle">推断项</p>
                {detail.implicitSteps.slice(0, 4).map((step, index) => (
                  <div key={`${step.implicitAbility}-${index}`} className="flex items-start gap-1.5">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0D9488]" />
                    <MathTextBlock
                      text={step.implicitAbility}
                      className="min-w-0 text-[11px] font-medium leading-4 text-muted"
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {!pending && !failedWithoutPap && !detail.hasContent ? (
          <p className="text-[11px] leading-4 text-muted">暂未识别到可展示的隐性能力。</p>
        ) : null}
      </div>
    </div>
  );
}

export function KnowledgeParticlePapDetailModal({
  visible,
  onClose,
  papView,
  loading = false,
  error = null,
  rebuilding = false,
  onRebuild,
  particleTitle,
  questionSnippet,
}: {
  visible: boolean;
  onClose: () => void;
  papView: KnowledgeParticlePapView | null;
  loading?: boolean;
  error?: string | null;
  rebuilding?: boolean;
  onRebuild?: () => void;
  particleTitle?: string;
  questionSnippet?: string;
}) {
  if (!visible) return null;
  const detail = buildPapDetail(papView?.pap ?? null);
  const pending = loading
    || papView?.extractionStatus === 'running'
    || papView?.extractionStatus === 'pending';
  const failed = papView?.extractionStatus === 'failed';
  const failedWithoutPap = failed && !detail.hasContent;

  return (
    <FloatingDetailCard
      title="隐性能力详情"
      subtitle="结合本题情境的 MindSet 分析"
      onClose={onClose}
      footer={onRebuild ? (
        <button
          type="button"
          disabled={loading || rebuilding}
          onClick={onRebuild}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {(loading || rebuilding) ? <Loader2 size={16} className="animate-spin" /> : null}
          重新构造隐性能力
        </button>
      ) : null}
    >
      {questionSnippet || particleTitle ? (
        <section className="space-y-1">
          <h3 className="text-sm font-extrabold text-ink">本题情境</h3>
          {particleTitle ? (
            <MathTextBlock text={particleTitle} className="text-sm font-semibold text-ink" />
          ) : null}
          {questionSnippet ? (
            <MathTextBlock text={questionSnippet} className="text-sm leading-relaxed text-muted" />
          ) : null}
        </section>
      ) : null}

      {pending ? (
        <div className="flex items-center gap-2 text-sm text-primary">
          <Loader2 size={16} className="animate-spin" />
          {extractionStatusLabel(papView?.extractionStatus) || '正在分析…'}
        </div>
      ) : null}

      {!pending && (failedWithoutPap || error) ? (
        <p className="text-sm text-danger">{papView?.failure?.message || error || '隐性能力分析失败'}</p>
      ) : null}

      {!pending && !failedWithoutPap && !detail.hasContent ? (
        <p className="text-sm text-muted">暂未识别到可展示的隐性能力分析。可尝试「重新构造隐性能力」。</p>
      ) : null}

      {!pending && detail.hasContent ? (
        <div className="space-y-4">
          {detail.flowSummary ? (
            <section className="rounded-xl border border-primary/15 bg-primary/5 p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-primary">能力流概览</h3>
              <MathTextBlock text={detail.flowSummary} className="mt-2 text-sm leading-relaxed text-ink" />
            </section>
          ) : null}

          {detail.implicitSteps.length ? (
            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-ink">逐步隐性能力分析</h3>
              {detail.implicitSteps.map((step, index) => (
                <div key={`${step.implicitAbility}-${index}`} className="flex gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {step.stepOrder ?? index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <MathTextBlock text={step.implicitAbility} className="text-sm font-medium text-ink" />
                    {step.reasoning ? (
                      <MathTextBlock text={step.reasoning} className="mt-1 text-xs leading-relaxed text-muted" />
                    ) : null}
                  </div>
                </div>
              ))}
            </section>
          ) : null}

          {detail.matchedTags.length ? (
            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-ink">MindSet 隐性能力匹配</h3>
              <div className="flex flex-wrap gap-2">
                {detail.matchedTags.map((tag, index) => (
                  <span
                    key={`${tag.label}-${index}`}
                    className="rounded-full border border-primary/15 bg-primary/5 px-2.5 py-1 text-xs text-primary"
                    title={tag.reason}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </section>
          ) : null}

          {detail.examPointSteps.length ? (
            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-ink">考点流</h3>
              {detail.examPointSteps.map((step, index) => (
                <div key={`${step.examPointLabel}-${index}`} className="flex gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black/[0.04] text-xs font-semibold text-muted">
                    {step.stepOrder ?? index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <MathTextBlock text={step.examPointLabel} className="text-sm text-ink" />
                    {step.reasoning ? (
                      <MathTextBlock text={step.reasoning} className="mt-0.5 text-xs text-muted" />
                    ) : null}
                  </div>
                </div>
              ))}
            </section>
          ) : null}
        </div>
      ) : null}
    </FloatingDetailCard>
  );
}

export function FloatingDetailCard({
  title,
  subtitle,
  onClose,
  children,
  footer,
  headerClassName = 'bg-[#EAF2FF]',
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  headerClassName?: string;
}) {
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-[#0B1320]/40"
        aria-label="关闭"
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-[min(640px,85dvh)] w-full max-w-[480px] flex-col overflow-hidden rounded-2xl border border-primary/15 bg-surface shadow-[0_18px_28px_rgba(7,17,31,0.22)]">
        <div className={`flex items-start gap-3 border-b border-primary/10 px-5 py-4 ${headerClassName}`}>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-extrabold tracking-wide text-primary-strong">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-xs font-semibold text-muted">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-primary-strong hover:bg-primary/10"
            aria-label="关闭"
          >
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>
        {footer ? <div className="border-t border-border px-5 py-3">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
